import config from "../core/config";
import {
  buildLocalActionItemFromSelection,
  CAPTURE_WORKFLOW_ID,
  CAPTURE_WORKFLOW_NAME,
  enrichActionItemRow,
} from "./components/actionItemHelpers";
import {
  isSessionAuthErrorMessage,
  maybeRedirectOnSessionAuthError,
} from "../core/services/sessionExpiry";
import {
  peekCrmWrittenLink,
  rememberCrmWrittenLink,
} from "./components/shared/crmWrittenLinkCache";
import { fetchJsonDesktopSafe } from "../core/services/desktopFetch";

export { CAPTURE_WORKFLOW_ID, CAPTURE_WORKFLOW_NAME };
export { isSessionAuthErrorMessage };

const jsonHeaders = {
  "Content-Type": "application/json",
};

/**
 * Absolute URL for business-mode HTTP routes (`/business/*` on the API server).
 *
 * Always keep the configured API base (including a trailing `/api` segment when present).
 * Rewriting to the static app origin serves `index.html` and breaks JSON parsing.
 *
 * @example
 * // BACKEND_URL "https://app.taskmantis.com/api", path "workflows"
 * businessModeApiUrl("workflows");
 * // "https://app.taskmantis.com/api/business/workflows"
 *
 * @example
 * // BACKEND_URL "http://localhost:8080", path "workflows"
 * businessModeApiUrl("workflows");
 * // "http://localhost:8080/business/workflows"
 */
const businessModeApiUrl = (pathFromBusiness) => {
  const suffix = String(pathFromBusiness || "").replace(/^\/+/, "");
  const base = String(config.BACKEND_URL || "").replace(/\/$/, "");
  return `${base}/business/${suffix}`;
};

/**
 * Parse a fetch response body as JSON; surface HTML error pages clearly.
 *
 * @example
 * // Input: response with body '{"error":"Gmail is not connected"}', ok=false
 * // Output: throws Error("Gmail is not connected")
 *
 * @example
 * // Input: response with body '<!doctype html>...', ok=false
 * // Output: throws Error("Failed to set up Gmail connector (HTTP 502)")
 */
const parseApiJsonResponse = async (response, fallbackMessage) => {
  const text = await response.text();
  let payload = {};
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch (_) {
      const trimmed = text.trim().toLowerCase();
      if (trimmed.startsWith("<!doctype") || trimmed.startsWith("<html")) {
        throw new Error(
          response.ok
            ? `${fallbackMessage}: server returned HTML instead of JSON`
            : `${fallbackMessage} (HTTP ${response.status})`
        );
      }
      throw new Error(fallbackMessage);
    }
  }
  if (!response.ok) {
    const message = payload?.error || fallbackMessage;
    maybeRedirectOnSessionAuthError(message);
    throw new Error(message);
  }
  return payload;
};

export const getBrowserTimezone = () => {
  /**
   * Resolve browser IANA timezone.
   *
   * @example
   * // Input
   * getBrowserTimezone();
   * // Output
   * // "America/Los_Angeles"
   */
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return String(timezone || "").trim() || "UTC";
  } catch (error) {
    return "UTC";
  }
};

export const getGmailStatus = async ({ verify } = {}) => {
  const url = new URL(businessModeApiUrl("gmail/status"));
  if (verify) {
    url.searchParams.set("verify", verify);
  }
  const response = await fetch(url.toString(), {
    method: "GET",
    headers: jsonHeaders,
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error("Failed to fetch Gmail status");
  }
  return response.json();
};

/**
 * True when Gmail is ready for performer thread search and draft save.
 *
 * @example
 * // Input: { gmail_performer_ready: true }
 * // Output: true
 *
 * @example
 * // Input: { gmail_performer_ready: false, gmail_has_draft_scope: true }
 * // Output: false
 */
export const isGmailReadyForPerformer = (status) => {
  if (!status || typeof status !== "object") return false;
  if (status.gmail_performer_ready === true) return true;
  if (status.gmail_performer_ready === false) return false;
  return status.gmail_has_draft_scope === true;
};

/**
 * Load Attio CRM write-back connection status.
 *
 * @example
 * await getAttioStatus();
 * // { connected: true, credential_id: "cred_…" }
 */
export const getAttioStatus = async () => {
  const response = await fetch(businessModeApiUrl("attio/status"), {
    method: "GET",
    headers: jsonHeaders,
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error("Failed to fetch Attio status");
  }
  return response.json();
};

/**
 * Load CRM write-back status for a provider (`attio` or `salesforce`).
 *
 * @example
 * await getCrmConnectorStatus("attio");
 * // { connected: true, writes_count: 2, last_written_at: "2026-07-24T10:30:00Z" }
 */
export const getCrmConnectorStatus = async (provider) => {
  const providerKey = String(provider || "").trim().toLowerCase();
  if (providerKey === "attio") {
    return getAttioStatus();
  }
  if (providerKey === "salesforce") {
    return getSalesforceStatus();
  }
  throw new Error(`Unsupported CRM provider: ${provider}`);
};

/**
 * Load recent CRM write activity for connector settings (write history + object links).
 *
 * @example
 * await getCrmConnectorActivity("attio");
 * // {
 * //   cumulative: { writes_count: 2, last_written_at: "2026-07-24T10:30:00Z" },
 * //   history: [{ item_key: "abc", record_url: "https://...", action_item_title: "Follow up" }],
 * // }
 */
export const getCrmConnectorActivity = async (provider) => {
  const providerKey = String(provider || "").trim().toLowerCase();
  if (!providerKey) {
    throw new Error("CRM provider is required");
  }
  const response = await fetch(businessModeApiUrl(`${providerKey}/activity`), {
    method: "GET",
    headers: jsonHeaders,
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch ${providerKey} activity`);
  }
  return response.json();
};

/**
 * Disconnect a CRM write-back connector for the current user.
 *
 * @example
 * await disconnectCrmConnector("attio");
 * // { message: "Attio disconnected", disconnected: true }
 */
export const disconnectCrmConnector = async (provider) => {
  const providerKey = String(provider || "").trim().toLowerCase();
  if (!providerKey) {
    throw new Error("CRM provider is required");
  }
  const response = await fetch(businessModeApiUrl(`${providerKey}/disconnect`), {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error?.error || `Failed to disconnect ${providerKey}`);
  }
  return response.json();
};

/**
 * Load whether Salesforce CRM write-back is connected for the current user.
 *
 * @example
 * await getSalesforceStatus();
 * // { connected: true, credential_id: "cred_…" }
 */
export const getSalesforceStatus = async () => {
  const response = await fetch(businessModeApiUrl("salesforce/status"), {
    method: "GET",
    headers: jsonHeaders,
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error("Failed to fetch Salesforce status");
  }
  return response.json();
};

/**
 * Load Google Calendar connection status for FreeBusy suggestions.
 *
 * @example
 * await getCalendarStatus();
 * // { connected: false, calendar_authorized: false }
 */
export const getCalendarStatus = async () => {
  const response = await fetch(businessModeApiUrl("calendar/status"), {
    method: "GET",
    headers: jsonHeaders,
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error("Failed to fetch Calendar status");
  }
  return response.json();
};

/**
 * Disconnect Google Calendar used for scheduling suggestions.
 *
 * @example
 * await disconnectBusinessPerformerCalendar();
 * // { disconnected: true }
 */
export const disconnectBusinessPerformerCalendar = async () => {
  const response = await fetch(businessModeApiUrl("calendar/performer/disconnect"), {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify({}),
  });
  return parseApiJsonResponse(response, "Failed to disconnect Calendar");
};

export const setupBusinessGmailDailyDigestWorkflow = async (payload) => {
  const response = await fetch(businessModeApiUrl("gmail/setup"), {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(payload),
  });
  return parseApiJsonResponse(response, "Failed to set up Gmail connector");
};

/**
 * Set up a business daily digest workflow for Zoom.
 *
 * @example
 * // Input
 * await setupBusinessZoomDailyDigestWorkflow({ connector_name: "Zoom Digest", frequency: "daily" });
 * // Output
 * // { connector_id: "...", transform_id: "...", source_id: "...", status: "active" }
 */
/**
 * Set up a business daily digest workflow for Google Calendar.
 *
 * @example
 * // Input
 * await setupBusinessGoogleCalendarDailyDigestWorkflow({ connector_name: "Calendar Digest", frequency: "daily" });
 * // Output
 * // { connector_id: "...", transform_id: "...", source_id: "...", status: "active" }
 */
export const setupBusinessGoogleCalendarDailyDigestWorkflow = async (payload) => {
  const response = await fetch(businessModeApiUrl("calendar/setup"), {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(payload),
  });
  return parseApiJsonResponse(response, "Failed to set up Google Calendar connector");
};

export const setupBusinessZoomDailyDigestWorkflow = async (payload) => {
  const response = await fetch(businessModeApiUrl("zoom/setup"), {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(payload),
  });
  return parseApiJsonResponse(response, "Failed to set up Zoom connector");
};

/**
 * Set up a business daily digest workflow for Google Meet.
 *
 * @example
 * // Input
 * await setupBusinessGoogleMeetDailyDigestWorkflow({ connector_name: "Meet Digest", frequency: "daily" });
 * // Output
 * // { connector_id: "...", transform_id: "...", source_id: "...", status: "active" }
 */
export const setupBusinessGoogleMeetDailyDigestWorkflow = async (payload) => {
  const response = await fetch(businessModeApiUrl("google_meet/setup"), {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(payload),
  });
  return parseApiJsonResponse(response, "Failed to set up Google Meet connector");
};

/**
 * Set up a business daily digest workflow for Gong.
 *
 * @example
 * // Input
 * await setupBusinessGongDailyDigestWorkflow({ connector_name: "Gong Digest", frequency: "daily" });
 * // Output
 * // { connector_id: "...", transform_id: "...", source_id: "...", status: "active" }
 */
export const setupBusinessGongDailyDigestWorkflow = async (payload) => {
  const response = await fetch(businessModeApiUrl("gong/setup"), {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(payload),
  });
  return parseApiJsonResponse(response, "Failed to set up Gong connector");
};

/**
 * Set up a business daily digest workflow for Granola.
 *
 * @example
 * // Input
 * await setupBusinessGranolaDailyDigestWorkflow({ connector_name: "Granola Digest", frequency: "daily", api_key: "grn_123" });
 * // Output
 * // { connector_id: "...", transform_id: "...", source_id: "...", status: "active" }
 */
export const setupBusinessGranolaDailyDigestWorkflow = async (payload) => {
  const response = await fetch(businessModeApiUrl("granola/setup"), {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(payload),
  });
  return parseApiJsonResponse(response, "Failed to set up Granola connector");
};

/**
 * Set up a business daily digest workflow for Notion.
 *
 * Nothing is chosen: the connector reads the user's Notion AI meeting recordings
 * from the last 14 days, and a first sync is queued right away.
 *
 * @example
 * // Input
 * await setupBusinessNotionDailyDigestWorkflow({ frequency: "daily", credential_id: "cred-1" });
 * // Output
 * // { connector_id: "...", transform_id: "...", source_id: "...", status: "active", sync: { status: "queued" } }
 */
export const setupBusinessNotionDailyDigestWorkflow = async (payload) => {
  const response = await fetch(businessModeApiUrl("notion/setup"), {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(payload),
  });
  return parseApiJsonResponse(response, "Failed to set up Notion connector");
};

/**
 * The Notion AI meeting recordings inside the connector's lookback window (its
 * "Syncs the last N days" setting), each with its status or the reason it was
 * skipped, plus whether Notion could be read at all.
 *
 * @example
 * await getNotionMeetings("wf-1");
 * // { windowDays: 7, status: { state: "ok" },
 * //   meetings: [{ id: "9f1c...", title: "Pipeline review", meeting_at: "2026-10-28T09:02:00+00:00", status: "extracted", reason: "" }] }
 */
export const getNotionMeetings = async (workflowId) => {
  const params = new URLSearchParams({ connector_id: String(workflowId || "") });
  const response = await fetch(
    businessModeApiUrl(`notion/meetings?${params.toString()}`),
    { method: "GET", credentials: "include", headers: jsonHeaders }
  );
  const data = await parseApiJsonResponse(response, "Failed to load Notion meetings");
  return {
    windowDays: data.window_days || null,
    status: data.status || { state: "pending" },
    meetings: data.meetings || [],
  };
};

/**
 * Set up a business daily digest workflow for Slack.
 *
 * v1 filters payload shape: {include_dms, include_group_dms, channel_allowlist}
 * -- DMs and group DMs sync by default; channels are opt-in only.
 *
 * @example
 * // Input
 * await setupBusinessSlackDailyDigestWorkflow({
 *   connector_name: "Slack Digest",
 *   frequency: "daily",
 *   filters: { include_dms: true, include_group_dms: true, channel_allowlist: [] },
 * });
 * // Output
 * // { connector_id: "...", transform_id: "...", source_id: "...", status: "active" }
 */
export const setupBusinessSlackDailyDigestWorkflow = async (payload) => {
  const response = await fetch(businessModeApiUrl("slack/setup"), {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(payload),
  });
  return parseApiJsonResponse(response, "Failed to set up Slack connector");
};

/**
 * Verify a Granola API key against the Granola public API.
 *
 * @example
 * // Input
 * await testGranolaApiKey("grn_123");
 * // Output
 * // { status: "success", message: "Granola API key is valid." }
 */
export const testGranolaApiKey = async (apiKey) => {
  const response = await fetch(`${config.BACKEND_URL}/externalauth/granola/test_connection`, {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify({ api_key: String(apiKey || "").trim() }),
  });
  const data = await response.json();
  if (!response.ok || data.status !== "success") {
    throw new Error(data.message || "Invalid Granola API key");
  }
  return data;
};

export const updateGmailSettings = async (payload) => {
  const response = await fetch(businessModeApiUrl("gmail/settings"), {
    method: "PATCH",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error?.error || "Failed to update Gmail settings");
  }
  return response.json();
};

/**
 * Disconnect Gmail for business mode and soft-delete related workflow entities.
 *
 * @example
 * // Input
 * await disconnectBusinessGmail();
 * // Output
 * // { message: "Business Gmail disconnected", connectors_deleted: 1, ... }
 */
export const disconnectBusinessGmail = async () => {
  const response = await fetch(businessModeApiUrl("gmail/disconnect"), {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error?.error || "Failed to disconnect Gmail");
  }
  return response.json();
};

/**
 * Disconnect performer Gmail from General settings (does not remove workflow connectors).
 *
 * @example
 * // Input
 * await disconnectBusinessPerformerGmail();
 * // Output
 * // { message: "Performer Gmail disconnected", disconnected: true }
 */
export const disconnectBusinessPerformerGmail = async () => {
  const response = await fetch(businessModeApiUrl("gmail/performer/disconnect"), {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error?.error || "Failed to disconnect Gmail");
  }
  return response.json();
};

/**
 * Disconnect Drive access from General settings (does not affect workflow connectors).
 *
 * @example
 * // Input
 * await disconnectBusinessPerformerDrive();
 * // Output
 * // { message: "Drive disconnected", disconnected: true }
 */
export const disconnectBusinessPerformerDrive = async () => {
  const response = await fetch(businessModeApiUrl("gmail/drive/disconnect"), {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error?.error || "Failed to disconnect Drive");
  }
  return response.json();
};

export const getBusinessWorkflows = async () => {
  const response = await fetch(businessModeApiUrl("workflows"), {
    method: "GET",
    headers: jsonHeaders,
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error("Failed to fetch workflows");
  }
  return response.json();
};

/**
 * Fetch one business workflow by id.
 *
 * @example
 * await getBusinessWorkflow("2af0f4f3-07ec-4f6b-8a94-4af9f4bc4de1");
 * // { workflow_id: "...", name: "Zoom Digest", lookback_days: 90, frequency: "daily" }
 */
export const getBusinessWorkflow = async (workflowId) => {
  const response = await fetch(
    businessModeApiUrl(`workflows/${String(workflowId || "").trim()}`),
    {
      method: "GET",
      headers: jsonHeaders,
      credentials: "include",
    }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to fetch workflow");
  }
  return response.json();
};

/**
 * Soft-delete a business workflow by id.
 *
 * @example
 * // Input
 * await deleteBusinessWorkflow("2af0f4f3-07ec-4f6b-8a94-4af9f4bc4de1");
 * // Output
 * // { message: "Workflow deleted", workflow_id: "2af0f4f3-07ec-4f6b-8a94-4af9f4bc4de1" }
 */
export const deleteBusinessWorkflow = async (workflowId) => {
  const response = await fetch(businessModeApiUrl(`workflows/${workflowId}`), {
    method: "DELETE",
    headers: jsonHeaders,
    credentials: "include",
  });
  // Empty/HTML 502/504 bodies used to throw "Unexpected end of JSON input".
  return parseApiJsonResponse(response, "Failed to delete workflow");
};

/**
 * Update business workflow settings (name, lookback, cadence).
 *
 * @example
 * // Input
 * await updateBusinessWorkflow("2af0f4f3-07ec-4f6b-8a94-4af9f4bc4de1", {
 *   name: "Founder Gmail Digest",
 *   lookback_days: 90,
 *   frequency: "daily",
 * });
 * // Output
 * // { message: "Workflow updated", workflow_id: "...", name: "Founder Gmail Digest", lookback_days: 90 }
 */
export const updateBusinessWorkflow = async (workflowId, payload = {}) => {
  const response = await fetch(businessModeApiUrl(`workflows/${workflowId}`), {
    method: "PATCH",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error?.error || "Failed to update workflow");
  }
  return response.json();
};

/**
 * Re-authenticate an existing workflow connector after OAuth refresh.
 *
 * @example
 * await reconnectBusinessWorkflow("wf-1", { credential_id: "cred-1" });
 * // { message: "Workflow reconnected", workflow_id: "wf-1", sync: { status: "queued" } }
 */
export const reconnectBusinessWorkflow = async (workflowId, payload = {}) => {
  const id = String(workflowId || "").trim();
  if (!id) {
    throw new Error("Workflow id is required");
  }
  const response = await fetch(businessModeApiUrl(`workflows/${id}/reconnect`), {
    method: "PATCH",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(payload),
  });
  return parseApiJsonResponse(response, "Failed to reconnect workflow");
};

/**
 * Fetch latest digest runs for a business workflow.
 *
 * @example
 * // Input
 * await getBusinessWorkflowRuns("2af0f4f3-07ec-4f6b-8a94-4af9f4bc4de1", 10);
 * // Output
 * // [{ digest_date: "2026-04-27", digest_subject: "Daily digest...", digest_payload_gcs_url: "gs://..." }]
 */
export const getBusinessWorkflowRuns = async (workflowId, limit = 20) => {
  const response = await fetch(
    businessModeApiUrl(`workflows/${workflowId}/runs?limit=${limit}`),
    {
      method: "GET",
      headers: jsonHeaders,
      credentials: "include",
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error?.error || "Failed to fetch workflow run history");
  }
  return response.json();
};

/**
 * Fetch sync stats (extract/digest/task counts) for a business workflow's settings page.
 *
 * @example
 * // Input
 * await getBusinessWorkflowStats("2af0f4f3-07ec-4f6b-8a94-4af9f4bc4de1");
 * // Output
 * // { last_connector_job: {...}, last_transform_job: {...}, cumulative: {...}, tasks: {...} }
 */
export const getBusinessWorkflowStats = async (workflowId) => {
  const response = await fetch(businessModeApiUrl(`workflows/${workflowId}/stats`), {
    method: "GET",
    headers: jsonHeaders,
    credentials: "include",
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error?.error || "Failed to fetch workflow stats");
  }
  return response.json();
};

/**
 * Fetch input message metadata for a selected run window.
 *
 * @example
 * // Input
 * await getRunInputMessages("2af0f4f3-07ec-4f6b-8a94-4af9f4bc4de1", { sourceMessageIds: ["msg-1", "msg-2"] });
 * // Output
 * // [{ message_id: "...", subject: "...", gcs_url: "gs://..." }]
 */
export const getRunInputMessages = async (
  workflowId,
  { digestPayloadGcsUrl = "", sourceMessageIds = [] } = {}
) => {
  const query = new URLSearchParams();
  const normalizedSourceMessageIds = Array.isArray(sourceMessageIds)
    ? sourceMessageIds.map((id) => String(id || "").trim()).filter(Boolean)
    : [];
  if (normalizedSourceMessageIds.length > 0) {
    query.set("source_message_ids", normalizedSourceMessageIds.join(","));
  } else if (digestPayloadGcsUrl) {
    query.set("digest_payload_gcs_url", String(digestPayloadGcsUrl || ""));
  }
  const wid = String(workflowId || "").trim();
  const path =
    wid === CAPTURE_WORKFLOW_ID
      ? `action-items/capture/runs/input-messages?${query.toString()}`
      : `workflows/${wid}/runs/input-messages?${query.toString()}`;
  const response = await fetch(businessModeApiUrl(path), {
    method: "GET",
    headers: jsonHeaders,
    credentials: "include",
  });
  if (!response.ok) {
    // Non-JSON bodies (proxy 502 pages) must not surface as a SyntaxError either.
    const body = await response.json().catch(() => ({}));
    const err = new Error(body?.error || "Failed to fetch run input messages");
    err.status = response.status;
    err.code = body?.code || null;
    throw err;
  }
  return response.json();
};

/**
 * Fetch raw source content for one input record.
 *
 * @example
 * // Input
 * await getRunInputMessageContent("2af0f4f3-07ec-4f6b-8a94-4af9f4bc4de1", "gs://bucket/path/message.json");
 * // Output
 * // { content: "{...raw message json...}" }
 */
export const getRunInputMessageContent = async (workflowId, gcsUrl) => {
  const query = new URLSearchParams({
    gcs_url: String(gcsUrl || ""),
  });
  const wid = String(workflowId || "").trim();
  const path =
    wid === CAPTURE_WORKFLOW_ID
      ? `action-items/capture/runs/input-message-content?${query.toString()}`
      : `workflows/${wid}/runs/input-message-content?${query.toString()}`;
  const response = await fetch(businessModeApiUrl(path), {
    method: "GET",
    headers: jsonHeaders,
    credentials: "include",
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const err = new Error(body?.error || "Failed to fetch source message content");
    err.status = response.status;
    err.code = body?.code || null;
    throw err;
  }
  return response.json();
};

export const getFrequencyOptions = async () => {
  const response = await fetch(`${config.BACKEND_URL}/connectors/frequency-options`, {
    method: "GET",
    headers: jsonHeaders,
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error("Failed to fetch frequency options");
  }
  return response.json();
};

/**
 * Update connector schedule fields used by business digest workflows.
 *
 * @example
 * await updateConnector("629b15c8-9b47-421c-b5d1-e0ceb51c6223", {
 *   name: "Zoom - user@co.com",
 *   lookback_days: 90,
 *   frequency: "daily",
 * });
 * // { message: "Connector updated successfully" }
 */
export const updateConnector = async (connectorId, payload = {}) => {
  const id = String(connectorId || "").trim();
  if (!id) {
    throw new Error("Connector id is required");
  }
  const response = await fetch(`${config.BACKEND_URL}/connectors/update/${id}`, {
    method: "PUT",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error?.error || error?.message || "Failed to update connector");
  }
  return response.json();
};

/**
 * Fetch the merged action-item todo list for the latest digest run.
 *
 * @example
 * // Input
 * await getWorkflowActionItems("2af0f4f3-07ec-4f6b-8a94-4af9f4bc4de1");
 * // Output
 * // [{ key: "abc123", action_item: "Send contract", status: "pending", suggested_done: true, suggestion_reason: "..." }]
 *
 * @example
 * // Input
 * await getWorkflowActionItems("2af0f4f3-07ec-4f6b-8a94-4af9f4bc4de1", { sinceCreatedAt: "2026-05-08T06:30:00Z" });
 * // Output
 * // [{ key: "def456", action_item: "Confirm budget approval", status: "pending" }]
 */
export const getAllActionItems = async () => {
  const response = await fetch(businessModeApiUrl("action-items"), {
    method: "GET",
    headers: jsonHeaders,
    credentials: "include",
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error?.error || "Failed to fetch action items");
  }
  const data = await response.json();
  return Array.isArray(data) ? data.map(enrichActionItemRow) : data;
};

/**
 * Fetch new and automatable action-item counts since the user's last visit.
 * The request also advances the server-side visit watermark for the next load.
 *
 * @example
 * // Input
 * await getActionItemsCatchUpSummary();
 * // Output
 * // { since_at: "2026-06-28T09:00:00+00:00", new_count: 12, automatable_count: 5, should_show: true }
 */
export const getActionItemsCatchUpSummary = async () => {
  const response = await fetch(businessModeApiUrl("action-items/catch-up"), {
    method: "GET",
    headers: jsonHeaders,
    credentials: "include",
  });
  return parseApiJsonResponse(response, "Failed to fetch action-item catch-up summary");
};

/**
 * Mark the user's catch-up summary as seen.
 *
 * @example
 * // Input
 * await acknowledgeActionItemsCatchUp();
 * // Output
 * // { last_seen_at: "2026-07-03T09:00:00+00:00" }
 */
export const acknowledgeActionItemsCatchUp = async () => {
  const response = await fetch(businessModeApiUrl("action-items/catch-up/ack"), {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
  });
  return parseApiJsonResponse(response, "Failed to acknowledge action-item catch-up");
};

export const getWorkflowActionItems = async (workflowId, { sinceCreatedAt = "" } = {}) => {
  const query = new URLSearchParams();
  if (String(sinceCreatedAt || "").trim()) {
    query.set("since_created_at", String(sinceCreatedAt).trim());
  }
  const querySuffix = query.toString() ? `?${query.toString()}` : "";
  const response = await fetch(
    businessModeApiUrl(`workflows/${workflowId}/action-items${querySuffix}`),
    {
      method: "GET",
      headers: jsonHeaders,
      credentials: "include",
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error?.error || "Failed to fetch action items");
  }
  const data = await response.json();
  return Array.isArray(data) ? data.map(enrichActionItemRow) : data;
};

/**
 * Create a manual action item via the unified API (optional workflow).
 *
 * Omit `workflowId` (or pass `""`) for personal captures (`workflow_id: "__capture__"`).
 * Pass a digest workflow UUID to store on that workflow's table.
 *
 * @example
 * // Input — personal capture
 * await createActionItem({
 *   actionItemText: "Follow up in Slack",
 *   actionTitle: "Follow up",
 *   sourceSnippet: "…",
 *   pageUrl: "https://app.slack.com/...",
 * });
 * // Output
 * // { storage: "manual", workflow_id: "__capture__", workflow_name: "Captured", key: "...", file_id: "manual::..." }
 *
 * @example
 * // Input — linked to a digest workflow
 * await createActionItem({
 *   actionItemText: "Email legal about NDA",
 *   workflowId: "2af0f4f3-07ec-4f6b-8a94-4af9f4bc4de1",
 * });
 * // Output
 * // { storage: "workflow", workflow_id: "2af0f4f3-...", workflow_name: "Gmail digest", ... }
 */
export const createActionItem = async ({
  actionItemText,
  actionTitle = "",
  sourceSnippet = "",
  pageUrl = "",
  workflowId = "",
  dueDate = "",
  messageSentAt = "",
  actionCategory = "",
  aiAssistType = "",
  aiAssistReason = "",
  fullSourceText = "",
} = {}) => {
  const body = { action_item_text: String(actionItemText || "").trim() };
  const title = String(actionTitle || "").trim();
  const snippet = String(sourceSnippet || "").trim();
  const url = String(pageUrl || "").trim();
  const wid = String(workflowId || "").trim();
  const due = String(dueDate || "").trim();
  const messageSentAtStr = String(messageSentAt || "").trim();
  const category = String(actionCategory || "").trim();
  const assistType = String(aiAssistType || "").trim();
  const assistReason = String(aiAssistReason || "").trim();
  const fullSourceTextStr = String(fullSourceText || "").trim();
  if (title) body.action_title = title;
  if (snippet) body.source_snippet = snippet;
  if (url) body.page_url = url;
  if (wid) body.workflow_id = wid;
  if (due) body.due_date = due;
  if (messageSentAtStr) body.message_sent_at = messageSentAtStr;
  // Only sent when the item was already classified during extraction (see
  // handleBulkAddGlobalActionItems) -- omitting it makes the server run its
  // own single-item classification call, same as before.
  if (category) body.action_category = category;
  if (assistType) body.ai_assist_type = assistType;
  if (assistReason) body.ai_assist_reason = assistReason;
  // The whole pasted transcript, when it's larger than sourceSnippet's short
  // grounding quote -- stored as the GCS body so "Original source" shows the
  // full document instead of just the quote.
  if (fullSourceTextStr) body.full_source_text = fullSourceTextStr;

  const response = await fetch(businessModeApiUrl("action-items"), {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error?.error || "Failed to create action item");
  }
  return response.json();
};

/**
 * Create a user-added action item for the given workflow (thin wrapper).
 *
 * @example
 * await createWorkflowActionItem("2af0f4f3-...", "Reach out to legal about NDA");
 * // { key: "abc123", file_id: "manual::...", workflow_id: "<uuid>", status: "pending" }
 */
export const createWorkflowActionItem = async (
  workflowId,
  actionItemText,
  { actionTitle = "", sourceSnippet = "", pageUrl = "" } = {}
) =>
  createActionItem({
    actionItemText,
    actionTitle,
    sourceSnippet,
    pageUrl,
    workflowId,
  });

/**
 * Create a personal captured action item without a digest workflow (thin wrapper).
 *
 * @example
 * await createCapturedActionItem("Follow up with design", { pageUrl: "https://slack.com/..." });
 * // { workflow_id: "__capture__", workflow_name: "Captured", key: "...", file_id: "manual::..." }
 */
export const createCapturedActionItem = async (
  actionItemText,
  { actionTitle = "", sourceSnippet = "", pageUrl = "" } = {}
) =>
  createActionItem({
    actionItemText,
    actionTitle,
    sourceSnippet,
    pageUrl,
  });

/**
 * Turn selected page text into a short title and full action-item body (API with local fallback).
 *
 * @example
 * await generateActionItemFromSelection("Please send the contract by Friday", {
 *   pageUrl: "https://mail.google.com/mail/u/0/",
 * });
 * // { action_title: "Send the contract", action_item_text: "Please send the contract by Friday" }
 */
export const generateActionItemFromSelection = async (selectionText, { pageUrl = "" } = {}) => {
  const text = String(selectionText || "").replace(/\s+/g, " ").trim();
  if (!text) {
    throw new Error("Selection is empty");
  }

  try {
    const response = await fetch(businessModeApiUrl("action-items/generate-from-selection"), {
      method: "POST",
      headers: jsonHeaders,
      credentials: "include",
      body: JSON.stringify({
        selection_text: text,
        page_url: String(pageUrl || "").trim(),
      }),
    });
    if (response.ok) {
      const data = await response.json();
      const action_title = String(data.action_title || "").trim();
      const action_item_text = String(
        data.action_item_text || data.action_item || ""
      ).trim();
      if (action_title && action_item_text) {
        return { action_title, action_item_text };
      }
    }
  } catch (_) {
    // Use local title/body synthesis when the API is unreachable or not deployed yet.
  }

  return buildLocalActionItemFromSelection(text, pageUrl);
};

/**
 * Transcribe a short voice recording for action-item capture.
 *
 * @example
 * // Input
 * const blob = new Blob(chunks, { type: "audio/webm" });
 * await transcribeActionItemAudio(blob);
 * // Output
 * // { transcript: "Follow up with legal on the NDA by Friday" }
 */
export const transcribeActionItemAudio = async (audioBlob) => {
  if (!audioBlob) {
    throw new Error("Audio recording is empty");
  }
  const formData = new FormData();
  formData.append("audio", audioBlob, "recording.webm");

  const response = await fetch(businessModeApiUrl("action-items/transcribe-audio"), {
    method: "POST",
    credentials: "include",
    body: formData,
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error?.error || "Failed to transcribe audio");
  }
  const data = await response.json();
  const transcript = String(data.transcript || "").trim();
  if (!transcript) {
    throw new Error("No speech detected");
  }
  return { transcript };
};

/**
 * Turn a voice transcript into action title and body text.
 *
 * @example
 * await generateActionItemFromTranscript("Email legal about the NDA tomorrow");
 * // { action_title: "Email legal about NDA", action_item_text: "Email legal about the NDA tomorrow." }
 */
export const generateActionItemFromTranscript = async (transcriptText) => {
  const transcript = String(transcriptText || "").replace(/\s+/g, " ").trim();
  if (!transcript) {
    throw new Error("Transcript is empty");
  }

  const response = await fetch(businessModeApiUrl("action-items/generate-from-transcript"), {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify({ transcript_text: transcript }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error?.error || "Failed to generate action item from transcript");
  }
  const data = await response.json();
  const action_title = String(data.action_title || "").trim();
  const action_item_text = String(data.action_item_text || data.action_item || "").trim();
  if (!action_title || !action_item_text) {
    throw new Error("Incomplete action item draft");
  }
  return { action_title, action_item_text };
};

/**
 * Extract multiple action items from a pasted meeting transcript.
 *
 * @example
 * await extractActionItemsFromMeetingTranscript("WEBVTT\\nSummary: Next Steps...");
 * // { action_items: [{ action_title: "Merge settings UI", action_item_text: "..." }] }
 */
export const extractActionItemsFromMeetingTranscript = async (transcriptText) => {
  const transcript = String(transcriptText || "").trim();
  if (!transcript) {
    throw new Error("Transcript is empty");
  }

  const response = await fetch(businessModeApiUrl("action-items/extract-from-transcript"), {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify({ transcript_text: transcript }),
  });
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const err = new Error(
      errorBody?.error || "Failed to extract action items from transcript"
    );
    err.status = response.status;
    err.code = errorBody?.code || null;
    throw err;
  }
  const data = await response.json();
  const action_items = (Array.isArray(data.action_items) ? data.action_items : [])
    .map((item) => ({
      action_title: String(item.action_title || "").trim(),
      action_item_text: String(item.action_item_text || item.action_item || "").trim(),
      source_snippet: String(item.source_snippet || "").trim(),
      action_category: String(item.action_category || "").trim(),
      ai_assist_type: item.ai_assist_type || null,
      ai_assist_reason: item.ai_assist_reason || null,
    }))
    .filter((item) => item.action_title && item.action_item_text);
  if (!action_items.length) {
    throw new Error("No action items found in that transcript");
  }
  return { action_items };
};

/**
 * Update the user-managed status for one action item.
 *
 * @example
 * // Input
 * await updateActionItemStatus("2af0f4f3-...", "abc123", "done", "Send contract to Acme", "msg-123");
 * // Output
 * // { key: "abc123", status: "done" }
 */
export const updateActionItemStatus = async (
  workflowId,
  itemKey,
  status,
  actionItemText = "",
  fileId = "",
  patchOptions = {},
  itemId = ""
) => {
  const { dueDate, actionTitle, noteText, actionCategory, account, pinned } =
    patchOptions && typeof patchOptions === "object" ? patchOptions : { dueDate: patchOptions };
  const resolvedNoteText =
    noteText !== undefined && noteText !== null
      ? String(noteText || "").trim()
      : String(actionItemText || "").trim();
  const body = { status, action_item_text: resolvedNoteText, file_id: fileId, item_id: itemId };
  if (dueDate !== undefined && dueDate !== null) body.due_date = dueDate;
  if (actionTitle !== undefined && actionTitle !== null) body.action_title = actionTitle;
  if (actionCategory !== undefined && actionCategory !== null) {
    body.action_category = String(actionCategory || "").trim();
  }
  if (account !== undefined && account !== null) {
    body.account = String(account || "").trim();
  }
  if (pinned !== undefined && pinned !== null) {
    body.pinned = Boolean(pinned);
  }
  const response = await fetch(
    businessModeApiUrl(`workflows/${workflowId}/action-items/${itemKey}`),
    {
      method: "PATCH",
      headers: jsonHeaders,
      credentials: "include",
      body: JSON.stringify(body),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error?.error || "Failed to update action item status");
  }
  return response.json();
};

/**
 * Update status for a captured action item (workflow_id `__capture__`).
 *
 * @example
 * // Input
 * await updateCapturedActionItemStatus("__capture__", "abc123", "done", "Send deck", "manual::uuid");
 * // Output
 * // { key: "abc123", status: "done" }
 */
export const updateCapturedActionItemStatus = async (
  _workflowId,
  itemKey,
  status,
  actionItemText = "",
  fileId = "",
  patchOptions = {},
  itemId = ""
) => {
  const { dueDate, actionTitle, noteText, actionCategory, account, pinned } =
    patchOptions && typeof patchOptions === "object" ? patchOptions : { dueDate: patchOptions };
  const resolvedNoteText =
    noteText !== undefined && noteText !== null
      ? String(noteText || "").trim()
      : String(actionItemText || "").trim();
  const body = { status, action_item_text: resolvedNoteText, file_id: fileId, item_id: itemId };
  if (dueDate !== undefined && dueDate !== null) body.due_date = dueDate;
  if (actionTitle !== undefined && actionTitle !== null) body.action_title = actionTitle;
  if (actionCategory !== undefined && actionCategory !== null) {
    body.action_category = String(actionCategory || "").trim();
  }
  if (account !== undefined && account !== null) {
    body.account = String(account || "").trim();
  }
  if (pinned !== undefined && pinned !== null) {
    body.pinned = Boolean(pinned);
  }
  const response = await fetch(
    businessModeApiUrl(`action-items/capture/${itemKey}`),
    {
      method: "PATCH",
      headers: jsonHeaders,
      credentials: "include",
      body: JSON.stringify(body),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error?.error || "Failed to update captured action item status");
  }
  return response.json();
};

/**
 * PATCH action-item status using the correct route for capture vs workflow rows.
 *
 * @example
 * // Input
 * await updateActionItemStatusForItem(
 *   { workflow_id: "__capture__", key: "abc", action_item: "Follow up", file_id: "manual::1" },
 *   "deleted"
 * );
 * // Output
 * // { key: "abc", status: "deleted" }
 */
export const updateActionItemStatusForItem = async (item, status, patchOptions = {}) => {
  const workflowId = String(item?.workflow_id || "").trim();
  const itemId = String(item?.item_id || "").trim();
  const mergedOptions = {
    actionCategory: String(item?.action_category || "").trim() || undefined,
    ...(typeof patchOptions === "object" ? patchOptions : { dueDate: patchOptions }),
  };
  if (workflowId === CAPTURE_WORKFLOW_ID) {
    return updateCapturedActionItemStatus(
      workflowId,
      item.key,
      status,
      item.action_item,
      item.file_id || "",
      mergedOptions,
      itemId
    );
  }
  return updateActionItemStatus(
    workflowId,
    item.key,
    status,
    item.action_item,
    item.file_id || "",
    mergedOptions,
    itemId
  );
};

/**
 * Update due date for one action item (keeps current status).
 *
 * @example
 * await updateActionItemDueDateForItem(item, "2026-06-01");
 * // { key: "abc123", status: "pending" }
 */
export const updateActionItemDueDateForItem = async (item, dueDate) => {
  const currentStatus = item.status || "pending";
  return updateActionItemStatusForItem(item, currentStatus, { dueDate });
};

/**
 * Toggle pin for one action item (keeps current status). Pinned items are exempt from aging.
 *
 * @example
 * await updateActionItemPinnedForItem(item, true);
 * // { key: "abc123", status: "pending", pinned: true }
 */
export const updateActionItemPinnedForItem = async (item, pinned) => {
  const currentStatus = item.status || "pending";
  return updateActionItemStatusForItem(item, currentStatus, {
    pinned: Boolean(pinned),
  });
};

/**
 * Update list title for one action item (keeps current status and due date).
 *
 * @example
 * await updateActionItemTitleForItem(item, "Follow up with legal");
 * // { key: "abc123", status: "pending", action_title: "Follow up with legal" }
 */
export const updateActionItemTitleForItem = async (item, actionTitle) => {
  const currentStatus = item.status || "pending";
  const dueDate = item.due_date !== undefined && item.due_date !== null ? item.due_date : "";
  return updateActionItemStatusForItem(item, currentStatus, {
    dueDate,
    actionTitle: String(actionTitle || "").trim(),
  });
};

/**
 * Update long-form note text for one action item (keeps current status, title, and due date).
 *
 * @example
 * await updateActionItemNoteForItem(item, "Follow up after legal confirms clause 3.");
 * // { key: "abc123", status: "pending", action_item: "Follow up after legal confirms clause 3." }
 */
export const updateActionItemNoteForItem = async (item, noteText) => {
  const currentStatus = item.status || "pending";
  const dueDate = item.due_date !== undefined && item.due_date !== null ? item.due_date : "";
  const actionTitle =
    item.action_title !== undefined && item.action_title !== null
      ? String(item.action_title || "").trim()
      : "";
  return updateActionItemStatusForItem(item, currentStatus, {
    dueDate,
    actionTitle,
    noteText: String(noteText || "").trim(),
  });
};

/**
 * Update category for one action item (keeps current status, title, due date, and note).
 *
 * @example
 * await updateActionItemCategoryForItem(item, "Client follow-ups");
 * // { key: "abc123", status: "pending" }
 */
export const updateActionItemCategoryForItem = async (item, actionCategory) => {
  const currentStatus = item.status || "pending";
  const dueDate = item.due_date !== undefined && item.due_date !== null ? item.due_date : "";
  const actionTitle =
    item.action_title !== undefined && item.action_title !== null
      ? String(item.action_title || "").trim()
      : "";
  return updateActionItemStatusForItem(item, currentStatus, {
    dueDate,
    actionTitle,
    noteText: String(item.action_item || "").trim(),
    actionCategory: String(actionCategory || "").trim(),
  });
};

/**
 * Update account name for one action item (keeps current status, title, due date, note, and category).
 *
 * @example
 * await updateActionItemAccountForItem(item, "Acme Corp");
 * // { key: "abc123", status: "pending" }
 *
 * @example
 * await updateActionItemAccountForItem(item, "");
 * // Clears account on the latest row
 */
export const updateActionItemAccountForItem = async (item, accountName) => {
  const currentStatus = item.status || "pending";
  const dueDate = item.due_date !== undefined && item.due_date !== null ? item.due_date : "";
  const actionTitle =
    item.action_title !== undefined && item.action_title !== null
      ? String(item.action_title || "").trim()
      : "";
  return updateActionItemStatusForItem(item, currentStatus, {
    dueDate,
    actionTitle,
    noteText: String(item.action_item || "").trim(),
    account: String(accountName || "").trim(),
  });
};

// Backward-compatible alias during rename transition.
export const setupGmailConnector = setupBusinessGmailDailyDigestWorkflow;

export const startPerformerRun = async ({
  actionItems,
  driveUrls = [],
  contextFileIds = [],
}) => {
  const response = await fetch(businessModeApiUrl("perform-action-items"), {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
    body: JSON.stringify({
      action_items: actionItems,
      drive_urls: driveUrls,
      context_file_ids: contextFileIds,
    }),
  });
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const err = new Error(errorBody?.error || "Failed to start performer run");
    err.status = response.status;
    err.code = errorBody?.code || null;
    throw err;
  }
  return response.json();
};

/**
 * Ask the admin to top up AI credits when the caller is at 0.
 *
 * @example
 * await requestMoreUsage();
 * // { message: "Request sent" }
 */
export const requestMoreUsage = async () => {
  const response = await fetch(businessModeApiUrl("usage/request-more"), {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data?.error || "Failed to request more credits");
    err.status = response.status;
    err.code = data?.code || null;
    throw err;
  }
  return data;
};

const readApiErrorMessage = async (response, fallback) => {
  try {
    const body = await response.json();
    const message = String(body?.error || fallback);
    maybeRedirectOnSessionAuthError(message);
    return message;
  } catch (_) {
    return fallback;
  }
};

export const getPerformerRunResult = async (runId) => {
  const response = await fetch(
    businessModeApiUrl(`perform-action-items/${runId}`),
    { method: "GET", headers: jsonHeaders, credentials: "include" }
  );
  if (!response.ok) {
    const message = await readApiErrorMessage(response, "Failed to get performer run status");
    throw new Error(message);
  }
  return response.json();
};

/**
 * Load persisted Action Item Performer rows (drafts, Drive URLs) from BigQuery.
 *
 * @example
 * // Input
 * await getActionItemsPerformerState();
 * await getActionItemsPerformerState({ workflowId: "2af0f4f3-..." });
 * // Output
 * // { items: [{ client_item_id, result, drive_urls, updated_at, ... }] }
 */
export const getActionItemsPerformerState = async ({ workflowId = "" } = {}) => {
  const query = new URLSearchParams();
  if (String(workflowId || "").trim()) {
    query.set("workflow_id", String(workflowId).trim());
  }
  const suffix = query.toString() ? `?${query.toString()}` : "";
  const response = await fetch(
    businessModeApiUrl(`action-items/performer-state${suffix}`),
    { method: "GET", headers: jsonHeaders, credentials: "include" }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error?.error || "Failed to load performer state");
  }
  return response.json();
};

/**
 * Load all offline performer schedules for the authenticated user's workflows.
 *
 * @example
 * // Input
 * await getGlobalActionItemPerformerSchedules();
 * // Output
 * // { schedules: [{ workflow_id, enabled, next_run_time, ... }], summary: { total: 3, enabled: 2 } }
 */
export const getGlobalActionItemPerformerSchedules = async () => {
  const response = await fetch(
    businessModeApiUrl("action-items/performer-schedules"),
    { method: "GET", headers: jsonHeaders, credentials: "include" }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error?.error || "Failed to load performer schedules");
  }
  return response.json();
};

/**
 * Pause or resume all offline performer schedules for the current user.
 *
 * @example
 * // Input
 * await patchGlobalActionItemPerformerSchedules({ enabled: false });
 * // Output
 * // { updated: 3, enabled: false }
 */
export const patchGlobalActionItemPerformerSchedules = async (body) => {
  const response = await fetch(
    businessModeApiUrl("action-items/performer-schedules"),
    {
      method: "PATCH",
      headers: jsonHeaders,
      credentials: "include",
      body: JSON.stringify(body),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error?.error || "Failed to update performer schedules");
  }
  return response.json();
};

/**
 * Delete all offline performer schedules for the current user.
 *
 * @example
 * // Input
 * await deleteGlobalActionItemPerformerSchedules();
 * // Output
 * // { deleted: 3 }
 */
export const deleteGlobalActionItemPerformerSchedules = async () => {
  const response = await fetch(
    businessModeApiUrl("action-items/performer-schedules"),
    { method: "DELETE", headers: jsonHeaders, credentials: "include" }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error?.error || "Failed to delete performer schedules");
  }
  return response.json();
};

/**
 * Load saved Drive context links for the action-item performer.
 *
 * @example
 * // Input
 * await getActionItemPerformerConfig();
 * // Output
 * // { drive_urls: ["https://docs.google.com/document/d/abc"], context_blocks: [] }
 */
export const getActionItemPerformerConfig = async () => {
  const response = await fetch(
    businessModeApiUrl("action-items/performer-config"),
    { method: "GET", headers: jsonHeaders, credentials: "include" }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error?.error || "Failed to load performer config");
  }
  return response.json();
};

/**
 * Save Drive context links without starting a performer run.
 *
 * @example
 * // Input
 * await patchActionItemPerformerConfig({ drive_urls: ["https://docs.google.com/document/d/abc"] });
 * // Output
 * // { drive_urls: ["https://docs.google.com/document/d/abc"], context_blocks: [] }
 */
export const patchActionItemPerformerConfig = async ({
  driveUrls,
  categoryCatalog,
  categoryDescriptions,
  recipientProfile,
  clearRecipientProfile,
  workingHoursStart,
  workingHoursEnd,
  defaultMeetingMinutes,
  clearMeetingPreferences,
} = {}) => {
  const body = {};
  if (driveUrls !== undefined) body.drive_urls = driveUrls;
  if (categoryCatalog !== undefined) body.category_catalog = categoryCatalog;
  if (categoryDescriptions !== undefined && categoryDescriptions !== null) {
    body.category_descriptions = categoryDescriptions;
  }
  if (recipientProfile !== undefined) body.recipient_profile = recipientProfile;
  if (clearRecipientProfile !== undefined) {
    body.clear_recipient_profile = clearRecipientProfile;
  }
  if (workingHoursStart !== undefined) body.working_hours_start = workingHoursStart;
  if (workingHoursEnd !== undefined) body.working_hours_end = workingHoursEnd;
  if (defaultMeetingMinutes !== undefined) {
    body.default_meeting_minutes = defaultMeetingMinutes;
  }
  if (clearMeetingPreferences !== undefined) {
    body.clear_meeting_preferences = clearMeetingPreferences;
  }
  const response = await fetch(
    businessModeApiUrl("action-items/performer-config"),
    {
      method: "PATCH",
      headers: jsonHeaders,
      credentials: "include",
      body: JSON.stringify(body),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error?.error || "Failed to save performer config");
  }
  return response.json();
};

/**
 * Reset saved performer profile + document context (for onboarding re-test).
 *
 * @example
 * await resetActionItemPerformerOnboarding();
 * // { drive_urls: [], context_files: [], recipient_profile: "" }
 */
export const resetActionItemPerformerOnboarding = async () => {
  const config = await getActionItemPerformerConfig();
  const files = Array.isArray(config?.context_files) ? config.context_files : [];
  await Promise.all(
    files
      .map((file) => String(file?.id || "").trim())
      .filter(Boolean)
      .map((fileId) => deletePerformerContextFile(fileId))
  );
  return patchActionItemPerformerConfig({
    driveUrls: [],
    clearRecipientProfile: true,
  });
};

/**
 * Upload performer context documents (multipart).
 *
 * @example
 * await uploadPerformerContextFiles([file]);
 * // { drive_urls: [], context_files: [{ id, filename, size_bytes, ... }], context_blocks: [] }
 */
export const uploadPerformerContextFiles = async (files) => {
  const formData = new FormData();
  (files || []).forEach((file) => {
    if (file) formData.append("files", file);
  });
  const response = await fetch(
    businessModeApiUrl("action-items/performer-context-files"),
    {
      method: "POST",
      credentials: "include",
      body: formData,
    }
  );
  if (!response.ok) {
    const message = await readApiErrorMessage(response, "Failed to upload context files");
    throw new Error(message);
  }
  return response.json();
};

/**
 * Delete one uploaded performer context file.
 *
 * @example
 * await deletePerformerContextFile("abc-123");
 * // { deleted: true, context_files: [] }
 */
export const deletePerformerContextFile = async (fileId) => {
  const response = await fetch(
    businessModeApiUrl(`action-items/performer-context-files/${encodeURIComponent(fileId)}`),
    {
      method: "DELETE",
      headers: jsonHeaders,
      credentials: "include",
    }
  );
  if (!response.ok) {
    const message = await readApiErrorMessage(response, "Failed to delete context file");
    throw new Error(message);
  }
  return response.json();
};

/**
 * Trigger an immediate sync for a business workflow.
 *
 * @example
 * await syncWorkflowNow("2af0f4f3-07ec-4f6b-8a94-4af9f4bc4de1");
 * // { status: "queued", job_id: "<uuid>" }
 * // or { status: "already_syncing" } when a job is already running
 */
/**
 * Trigger an immediate sync for a business workflow.
 * Returns queued_at_ms which should be passed to getWorkflowSyncStatus.
 *
 * @example
 * const { queued_at_ms } = await syncWorkflowNow("2af0f4f3-07ec-4f6b-8a94-4af9f4bc4de1");
 * // { status: "queued", connector_job_id: "<uuid>", queued_at_ms: 1234567890000 }
 */
export const syncWorkflowNow = async (workflowId) => {
  const id = String(workflowId || "").trim();
  if (!id) throw new Error("workflowId is required");
  const response = await fetch(businessModeApiUrl(`workflows/${id}/sync`), {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
  });
  if (!response.ok) {
    const message = await readApiErrorMessage(response, "Failed to trigger sync");
    throw new Error(message);
  }
  return response.json();
};

/**
 * Get two-phase sync status for a workflow (connector phase → transform phase).
 *
 * Params from syncWorkflowNow should be forwarded here to scope the lookup:
 *   - connectorJobId: connector_job_id returned by syncWorkflowNow
 *   - sinceMs:        queued_at_ms returned by syncWorkflowNow
 *   - transformJobId: transform_job_id from an already_syncing phase="transform" response
 *
 * @example
 * // Queued flow
 * const { connector_job_id, queued_at_ms } = await syncWorkflowNow(id);
 * await getWorkflowSyncStatus(id, { connectorJobId: connector_job_id, sinceMs: queued_at_ms });
 * // { status: "RUNNING", phase: "connector", connector: { ... } }
 * // { status: "RUNNING", phase: "transform", connector: { ... }, transform: { ... } }
 * // { status: "DONE",    phase: "done", ... }
 *
 * // Already-syncing transform flow
 * await getWorkflowSyncStatus(id, { connectorJobId, sinceMs, transformJobId });
 */
export const getWorkflowSyncStatus = async (workflowId, { connectorJobId, sinceMs, transformJobId } = {}) => {
  const id = String(workflowId || "").trim();
  if (!id) throw new Error("workflowId is required");
  const url = new URL(businessModeApiUrl(`workflows/${id}/sync/status`));
  if (connectorJobId) url.searchParams.set("connector_job_id", String(connectorJobId));
  if (sinceMs) url.searchParams.set("since_ms", String(sinceMs));
  if (transformJobId) url.searchParams.set("transform_job_id", String(transformJobId));
  // GET must not send Content-Type: application/json — that forces a CORS
  // preflight, and a failed preflight surfaces as "Failed to fetch".
  const response = await fetch(url.toString(), {
    method: "GET",
    credentials: "include",
  });
  if (!response.ok) {
    const message = await readApiErrorMessage(response, "Failed to fetch sync status");
    throw new Error(message);
  }
  return response.json();
};

// --- Google Calendar helpers ---

const _CALENDAR_DURATION_MAP = [
  ["meet", 30],
  ["call", 30],
  ["follow", 20],
  ["work", 45],
  ["personal", 15],
];
const _CALENDAR_DEFAULT_DURATION = 30;

const _CALENDAR_EVENT_TYPE_LABELS = {
  call: "Call",
  sync: "Sync",
  "catch-up": "Catch-up",
  catchup: "Catch-up",
  demo: "Demo",
  kickoff: "Kickoff",
  "kick-off": "Kickoff",
  meeting: "Meeting",
};

const _CALENDAR_SCHEDULING_TAIL_RE =
  /\s+(?:(?:for|at|by|before|after)\s+(?:tonight|tomorrow|today)(?:\s+at\s+.+)?|(?:for|at|by)\s+(?:next|this)\s+\w+(?:\s+at\s+.+)?|(?:today|tomorrow|tonight)(?:\s+at\s+.+)?|(?:next|this)\s+\w+|in\s+\d+\s+(?:days?|weeks?))\s*$/i;

const _CALENDAR_PLATFORM_TAIL_RE =
  /\s+on\s+(?:zoom|google\s+meet|microsoft\s+teams|teams|webex|skype)\s*$/i;

const _CALENDAR_READY_TITLE_RE =
  /^(Meeting|Call|Sync|Demo|Catch-up|Kickoff)\s+with\s+.+$/i;

const _calendarFirstSentence = (text) =>
  String(text || "")
    .split(/[.!?]/)[0]
    .trim() || String(text || "").trim();

const _stripCalendarSchedulingTail = (text) => {
  let cleaned = String(text || "").trim();
  while (true) {
    const next = cleaned
      .replace(_CALENDAR_SCHEDULING_TAIL_RE, "")
      .replace(_CALENDAR_PLATFORM_TAIL_RE, "")
      .trim();
    if (next === cleaned) break;
    cleaned = next;
  }
  return cleaned;
};

const _stripCalendarPlatformPhrase = (text) =>
  String(text || "")
    .replace(/\s+on\s+(?:zoom|google\s+meet|microsoft\s+teams|teams|webex|skype)\b/gi, "")
    .trim();

const _isSchedulingOnlyPurpose = (purpose) => {
  const t = String(purpose || "").trim().toLowerCase();
  if (!t) return true;
  if (/^(?:tonight|tomorrow|today|next\s+|this\s+)/.test(t)) return true;
  if (/\b(?:am|pm|o'clock)\b/.test(t)) return true;
  return /^\d/.test(t);
};

const _titleizeCalendarSubject = (text) =>
  _stripCalendarSchedulingTail(text)
    .split(/\s+/)
    .map((word) => (word === word.toLowerCase() ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(" ");

const _detectCalendarEventTypeLabel = (text) => {
  const lowered = String(text || "").toLowerCase();
  for (const [keyword, label] of Object.entries(_CALENDAR_EVENT_TYPE_LABELS)) {
    if (lowered.includes(keyword)) return label;
  }
  return "Meeting";
};

const _extractCalendarMeetingPurpose = (text) => {
  const sentence = _stripCalendarPlatformPhrase(_calendarFirstSentence(text));
  const patterns = [
    /\bfor\s+(?:the\s+)?(.+)$/i,
    /\babout\s+(.+)$/i,
    /\bregarding\s+(.+)$/i,
    /\bto\s+discuss\s+(.+)$/i,
  ];
  for (const pattern of patterns) {
    const match = sentence.match(pattern);
    if (!match) continue;
    let purpose = _stripCalendarSchedulingTail(match[1]);
    purpose = purpose.replace(/^(?:tomorrow'?s?|today'?s?)\s+/i, "").trim();
    if (purpose && !_isSchedulingOnlyPurpose(purpose)) return purpose;
  }
  return "";
};

const _extractCalendarPersonLabel = (text) => {
  const sentence = _calendarFirstSentence(text);
  const patterns = [
    /^send\s+a\s+(?:calendar|meeting)\s+invite\s+to\s+(.+)$/i,
    /^send\s+(.+?)\s+a\s+(?:calendar|meeting)\s+invite\b/i,
    /^(?:meet(?:ing)?|call|sync|demo|catch-?up)\s+with\s+(.+)$/i,
    /^(?:schedule|set\s+up|book|organi[sz]e)(?:\s+a)?(?:\s+(?:meeting|call|sync|demo|catch-?up|kick-?off))?\s+with\s+(.+)$/i,
  ];
  for (const pattern of patterns) {
    const match = sentence.match(pattern);
    if (!match) continue;
    const person = _stripCalendarSchedulingTail(_stripCalendarPlatformPhrase(match[1]));
    if (person) return person;
  }
  return "";
};

/**
 * Derive a short calendar event title from raw action item text.
 *
 * @example
 * deriveCalendarEventTitle("Meet with abhinav on zoom for tasks product feedback");
 * // "Tasks Product Feedback"
 */
export const deriveCalendarEventTitle = (text) => {
  const raw = String(text || "").replace(/\s+/g, " ").trim();
  if (!raw) return "";

  if (_CALENDAR_READY_TITLE_RE.test(raw) && raw.split(/\s+/).length <= 8) {
    return raw;
  }

  const normalized = raw.replace(/^(please|kindly)\s+/i, "");
  const eventType = _detectCalendarEventTypeLabel(normalized);
  const sentence = _calendarFirstSentence(normalized);

  const purpose = _extractCalendarMeetingPurpose(normalized);
  if (purpose) return _titleizeCalendarSubject(purpose);

  const personTopic = sentence.match(
    /^send\s+(.+?)\s+a\s+(?:calendar|meeting)\s+invite\s+for\s+(.+)$/i
  );
  if (personTopic) {
    const topic = _titleizeCalendarSubject(
      personTopic[2].replace(/^(?:tomorrow'?s?|today'?s?)\s+/i, "")
    );
    if (topic && !_isSchedulingOnlyPurpose(topic)) return topic;
  }

  const eventFor = sentence.match(/^create\s+a\s+calendar\s+event\s+for\s+(.+)$/i);
  if (eventFor) {
    return _titleizeCalendarSubject(eventFor[1]);
  }

  const person = _extractCalendarPersonLabel(sentence);
  if (person) {
    return `${eventType} with ${_titleizeCalendarSubject(person)}`;
  }

  return _titleizeCalendarSubject(sentence);
};

// Timezone abbreviation → UTC offset hours (matches Python _TZ_ABBR_OFFSETS)
const _TZ_ABBR_OFFSETS = {
  IST: 5.5, EST: -5, EDT: -4, CST: -6, CDT: -5,
  MST: -7, MDT: -6, PST: -8, PDT: -7,
  UTC: 0, GMT: 0, BST: 1, CET: 1, CEST: 2,
  JST: 9, SGT: 8, HKT: 8, AEST: 10, AEDT: 11, KST: 9,
};

/**
 * Parse an explicit clock time from action item text.
 * Handles "11 AM IST", "2pm", "3:30 PM EST", "14:00 UTC".
 * Returns { startIso, endIso } in UTC ISO format, or null if not found.
 */
const _extractExplicitTimeFromText = (text, dueDate, defaultDurationMinutes = 30) => {
  if (!text) return null;

  // 12h format: "11 AM IST", "2:30 PM", "9am"
  const m12 = text.match(/\b(\d{1,2})(?::(\d{2}))?\s*(am|pm)\s*([A-Z]{2,5})?\b/i);
  // 24h format with colon: "14:00", "09:30 IST"
  const m24 = !m12 && text.match(/\b((?:[01]?\d)|(?:2[0-3])):([0-5]\d)\s*([A-Z]{2,5})?\b/);

  let hour, minute, tzAbbr;
  if (m12) {
    hour = parseInt(m12[1], 10);
    minute = parseInt(m12[2] || "0", 10);
    const ampm = m12[3].toLowerCase();
    tzAbbr = (m12[4] || "").toUpperCase() || null;
    if (ampm === "pm" && hour !== 12) hour += 12;
    else if (ampm === "am" && hour === 12) hour = 0;
  } else if (m24) {
    hour = parseInt(m24[1], 10);
    minute = parseInt(m24[2], 10);
    tzAbbr = (m24[3] || "").toUpperCase() || null;
  } else {
    return null;
  }

  if (hour < 0 || hour > 23) return null;

  // Determine base date
  let baseDate;
  if (dueDate) {
    baseDate = new Date(dueDate + "T00:00:00Z");
    if (isNaN(baseDate.getTime())) baseDate = new Date();
  } else {
    baseDate = new Date();
  }
  const y = baseDate.getUTCFullYear();
  const mo = baseDate.getUTCMonth() + 1;
  const d = baseDate.getUTCDate();

  // Convert to UTC
  const offsetHours = tzAbbr && _TZ_ABBR_OFFSETS[tzAbbr] !== undefined
    ? _TZ_ABBR_OFFSETS[tzAbbr]
    : 0; // unknown TZ → treat as UTC

  const localTotalMinutes = hour * 60 + minute;
  const offsetMinutes = Math.round(offsetHours * 60);
  const utcTotalMinutes = localTotalMinutes - offsetMinutes;

  // Naive UTC datetime on the base date, then adjust for offset crossing midnight
  const pad = (n) => String(n).padStart(2, "0");
  const baseMs = Date.UTC(y, mo - 1, d, 0, 0, 0) + utcTotalMinutes * 60000;
  const startUtc = new Date(baseMs);
  const endUtc = new Date(baseMs + defaultDurationMinutes * 60000);

  if (isNaN(startUtc.getTime())) return null;

  const fmt = (dt) =>
    `${dt.getUTCFullYear()}${pad(dt.getUTCMonth() + 1)}${pad(dt.getUTCDate())}T${pad(dt.getUTCHours())}${pad(dt.getUTCMinutes())}00Z`;

  return { startIso: fmt(startUtc), endIso: fmt(endUtc), startDate: startUtc };
};

const _calendarDurationMinutes = (actionCategory) => {
  const cat = String(actionCategory || "").toLowerCase();
  for (const [keyword, minutes] of _CALENDAR_DURATION_MAP) {
    if (cat.includes(keyword)) return minutes;
  }
  return _CALENDAR_DEFAULT_DURATION;
};

const _formatGCalDatetime = (isoDate, extraMinutes = 0) => {
  const parts = String(isoDate || "").split("-").map(Number);
  if (parts.length < 3 || parts.some(isNaN)) return null;
  const [year, month, day] = parts;
  const totalMinutes = 9 * 60 + extraMinutes;
  const hh = Math.floor(totalMinutes / 60);
  const mm = totalMinutes % 60;
  const pad = (n) => String(n).padStart(2, "0");
  // No timezone suffix — Google Calendar interprets as the user's local time
  return `${year}${pad(month)}${pad(day)}T${pad(hh)}${pad(mm)}00`;
};

/**
 * Build a Google Calendar eventedit pre-fill URL from an action item.
 *
 * Returns null when the item has no title. When no due date is set, the URL
 * pre-fills the event title only and leaves date/time for the user to pick.
 *
 * @example
 * buildCalendarPrefillUrl(
 *   { action_item: "Call with Acme", action_category: "Client follow-ups", due_date: "2026-06-10" },
 *   { attendeeEmails: ["jane@acme.com"], emailSubject: "Re: proposal", starmeshLink: "https://..." }
 * );
 * // "https://calendar.google.com/calendar/r/eventedit?text=Call+with+Acme&dates=..."
 *
 * @example
 * buildCalendarPrefillUrl({ action_item: "Schedule sync with Acme" });
 * // "https://calendar.google.com/calendar/r/eventedit?text=Schedule+sync+with+Acme"
 */
const _formatUtcIsoToGCal = (isoStr) => {
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, "0");
  // Append Z so Google Calendar interprets as UTC and converts to user's calendar TZ
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;
};

export const buildCalendarPrefillUrl = (item, { attendeeEmails = [], emailSubject = "", starmeshLink = "" } = {}) => {
  const rawTitle = String(item?.action_item || item?.action_title || "").trim();
  const title = deriveCalendarEventTitle(rawTitle);
  if (!title) return null;

  const dueDate = String(item?.due_date || "").trim();
  const category = String(item?.action_category || "").trim();

  const descParts = [];
  if (emailSubject) descParts.push(`From: ${emailSubject}`);
  if (starmeshLink) descParts.push(`Task Mantis: ${starmeshLink}`);
  const description = descParts.join("\n");

  const params = new URLSearchParams();
  params.set("text", title);

  const duration = _calendarDurationMinutes(category);

  // Priority 1: parse explicit time directly from the action item text (works even on stale cache)
  const rawActionText = String(item?.action_item || item?.action_title || "").trim();
  const textTime = _extractExplicitTimeFromText(rawActionText, dueDate, duration);

  // Priority 2: performer-computed slot (freebusy result for no-explicit-time case)
  const scheduledStart = !textTime && (item?.scheduled_start_at || null);
  const scheduledEnd = !textTime && (item?.scheduled_end_at || null);

  if (textTime) {
    params.set("dates", `${textTime.startIso}/${textTime.endIso}`);
  } else if (scheduledStart && scheduledEnd) {
    const startStr = _formatUtcIsoToGCal(scheduledStart);
    const endStr = _formatUtcIsoToGCal(scheduledEnd);
    if (startStr && endStr) {
      params.set("dates", `${startStr}/${endStr}`);
    }
  } else if (dueDate) {
    const startStr = _formatGCalDatetime(dueDate, 0);
    const endStr = _formatGCalDatetime(dueDate, duration);
    if (startStr && endStr) {
      params.set("dates", `${startStr}/${endStr}`);
    }
  }

  if (description) params.set("details", description);
  const validAttendees = (attendeeEmails || []).filter(Boolean).join(",");
  if (validAttendees) params.set("add", validAttendees);

  return `https://calendar.google.com/calendar/r/eventedit?${params.toString()}`;
};

const calendarEventCache = new Map();
const calendarEventInflight = new Map();
const crmLinkInflight = new Map();

/**
 * Read a previously fetched calendar event for a task without hitting the network.
 *
 * @example
 * peekCachedCalendarEvent("w::item-key::f");
 * // { event_url: "https://calendar.google.com/..." } or null or undefined (never fetched)
 *
 * @param {string} itemKey
 * @returns {object | null | undefined}
 */
export const peekCachedCalendarEvent = (itemKey) => {
  const key = String(itemKey || "").trim();
  if (!key || !calendarEventCache.has(key)) return undefined;
  return calendarEventCache.get(key);
};

/**
 * Remember a calendar event (or a confirmed miss) so task switches reuse it.
 *
 * @example
 * rememberCalendarEventForItem("w::item-key::f", { event_url: "https://calendar.google.com/..." });
 *
 * @param {string} itemKey
 * @param {object | null} event
 */
export const rememberCalendarEventForItem = (itemKey, event) => {
  const key = String(itemKey || "").trim();
  if (!key) return;
  calendarEventCache.set(key, event || null);
};

/**
 * Fetch the server-created calendar event for a personal time block.
 *
 * Returns null when the item has no linked event yet (404 is treated as null).
 * Results are cached for the session so switching tasks does not refetch.
 *
 * @example
 * const event = await getCalendarEventForItem("w::item-key::f");
 * // { event_id: "abc", event_url: "https://calendar.google.com/..." }
 */
export const getCalendarEventForItem = async (itemKey) => {
  const key = String(itemKey || "").trim();
  if (!key) return null;
  if (calendarEventCache.has(key)) return calendarEventCache.get(key);
  if (calendarEventInflight.has(key)) return calendarEventInflight.get(key);

  const request = (async () => {
    const response = await fetch(
      businessModeApiUrl(`tasks/${encodeURIComponent(key)}/calendar-event`),
      { credentials: "include" }
    );
    if (response.status === 404 || !response.ok) return null;
    return response.json();
  })()
    .then((event) => {
      calendarEventCache.set(key, event || null);
      return event || null;
    })
    .finally(() => {
      calendarEventInflight.delete(key);
    });

  calendarEventInflight.set(key, request);
  return request;
};

/**
 * Begin the Attio CRM OAuth connect flow.
 *
 * Fetches the authorize URL from the backend. By default redirects the browser
 * to Attio's consent screen. Pass ``redirect: false`` to open the URL in a
 * popup from the Attio setup panel (same pattern as Zoom/Granola).
 *
 * @example
 * await beginAttioConnect(); // full-page redirect
 *
 * @example
 * const url = await beginAttioConnect({ redirect: false });
 * window.open(url, "_blank");
 */
export const beginAttioConnect = async ({
  oauthState = "business_attio",
  redirect = true,
} = {}) => {
  const data = await fetchJsonDesktopSafe(
    `${config.BACKEND_URL}/externalauth/attio/auth_url?oauth_state=${encodeURIComponent(oauthState)}`
  ).catch((error) => {
    throw new Error(
      String(error?.message || "").trim() || "Could not start Attio connection"
    );
  });
  const authUrl = data.auth_url || "";
  if (!authUrl) {
    throw new Error(data.error || "Could not start Attio connection");
  }
  if (redirect && typeof window !== "undefined") {
    window.location.assign(authUrl);
  }
  return authUrl;
};

/**
 * Start the Salesforce OAuth flow for CRM write-back — same shape as `beginAttioConnect`.
 *
 * @example
 * await beginSalesforceConnect(); // full-page redirect
 *
 * @example
 * const url = await beginSalesforceConnect({ redirect: false });
 * window.open(url, "_blank");
 */
export const beginSalesforceConnect = async ({
  oauthState = "business_salesforce",
  redirect = true,
} = {}) => {
  const data = await fetchJsonDesktopSafe(
    `${config.BACKEND_URL}/externalauth/salesforce/auth_url?oauth_state=${encodeURIComponent(oauthState)}`
  ).catch((error) => {
    throw new Error(
      String(error?.message || "").trim() || "Could not start Salesforce connection"
    );
  });
  const authUrl = data.auth_url || "";
  if (!authUrl) {
    throw new Error(data.error || "Could not start Salesforce connection");
  }
  if (redirect && typeof window !== "undefined") {
    window.location.assign(authUrl);
  }
  return authUrl;
};

/**
 * Fetch the CRM record linked to a CRM action item, if one has been written.
 *
 * Returns null when nothing has been written yet (404 is treated as null), so
 * the UI can show "already written to CRM" state on reload.
 *
 * @example
 * const link = await getCrmLinkForItem("w::item-key::f");
 * // { crm_record_id: "rec_1", crm_object_type: "people", provider: "attio", ... }
 */
export const getCrmLinkForItem = async (itemKey) => {
  const key = String(itemKey || "").trim();
  if (!key) return null;
  const cached = peekCrmWrittenLink(key);
  if (cached?.recordId && cached?.webUrl && cached?.writtenAt) {
    return {
      crm_record_id: cached.recordId,
      record_url: cached.webUrl,
      updated_at: cached.writtenAt || null,
      was_created: cached.wasCreated ?? null,
    };
  }
  if (crmLinkInflight.has(key)) return crmLinkInflight.get(key);

  const request = (async () => {
    const response = await fetch(
      businessModeApiUrl(`tasks/${encodeURIComponent(key)}/crm-link`),
      { credentials: "include" }
    );
    if (response.status === 404 || !response.ok) return null;
    return response.json();
  })()
    .then((link) => {
      if (link?.crm_record_id) {
        rememberCrmWrittenLink(key, {
          proposalKey: peekCrmWrittenLink(key)?.proposalKey || "",
          recordId: link.crm_record_id,
          webUrl: link.record_url || "",
          writtenAt: link.updated_at || "",
          wasCreated: typeof link.was_created === "boolean" ? link.was_created : null,
        });
      }
      return link;
    })
    .finally(() => {
      crmLinkInflight.delete(key);
    });

  crmLinkInflight.set(key, request);
  return request;
};

/**
 * Live search-as-you-type lookup against the user's connected CRM, for the
 * record-name field on the AI-assist CRM edit panel — lets picking an existing
 * person/company/deal replace a "create new" proposal instead of duplicating it.
 *
 * Attio only today. Never throws on a "no results" outcome — always resolves to
 * `{ candidates: [] }` when nothing is connected, nothing matches, or the search
 * itself fails, since this only backs an optional suggestion dropdown.
 *
 * @example
 * await searchCrmRecords({ objectType: "companies", query: "Acme" });
 * // { candidates: [{ record_id: "rec_1", label: "Acme Corp" }] }
 */
export const searchCrmRecords = async ({ objectType, query, provider = "attio" }) => {
  const params = new URLSearchParams({
    object_type: String(objectType || ""),
    query: String(query || ""),
    provider: String(provider || "attio"),
  });
  const response = await fetch(
    businessModeApiUrl(`crm/search-records?${params.toString()}`),
    { method: "GET", credentials: "include" }
  );
  if (!response.ok) return { candidates: [] };
  const data = await response.json().catch(() => ({}));
  return { candidates: Array.isArray(data?.candidates) ? data.candidates : [] };
};

/**
 * Post a user-confirmed Slack reply for a task. THE ONLY place a live Slack post happens.
 *
 * `payload` is the (possibly user-edited) proposed payload the performer built:
 * { channel_id, thread_ts, message }. Always replies into the same channel/thread the
 * action item was extracted from -- never resolves a new recipient. Resolves to the
 * posted message's ts/permalink, or throws with the server error message.
 *
 * @example
 * await sendSlackMessage("w::item-key::f", { channel_id: "C0123", thread_ts: "169999.0001", message: "..." });
 * // { slack_message_ts: "1700000001.000200", slack_channel_id: "C0123",
 * //   slack_permalink: "https://...", slack_sent_at: "2023-11-14T22:13:21+00:00" }
 */
export const sendSlackMessage = async (itemKey, payload) => {
  const response = await fetch(
    businessModeApiUrl(`tasks/${encodeURIComponent(itemKey)}/slack-send`),
    {
      method: "POST",
      credentials: "include",
      headers: jsonHeaders,
      body: JSON.stringify(payload || {}),
    }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.error || "Failed to send Slack reply");
    err.status = response.status;
    err.code = data?.code || null;
    err.missingScope = Boolean(data?.missing_scope);
    throw err;
  }
  return data;
};

/**
 * Search the connected Slack workspace for channels/people to send to.
 *
 * Backs the "Send via Slack" picker's search box — read-only, only ever
 * touches channel/user metadata, never message content. An empty `query`
 * returns the first page of each, unfiltered.
 *
 * @example
 * await searchSlackTargets("jane");
 * // { channels: [{ id: "C0123", name: "jane-project", type: "channel" }],
 * //   people: [{ user_id: "U0456", name: "Jane Doe", email: "jane@acme.com" }] }
 */
export const searchSlackTargets = async (query = "") => {
  const params = new URLSearchParams({ query: String(query || "") });
  const response = await fetch(
    businessModeApiUrl(`slack/search?${params.toString()}`),
    { method: "GET", credentials: "include", headers: jsonHeaders }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.error || "Failed to search Slack");
    err.status = response.status;
    throw err;
  }
  return { channels: data.channels || [], people: data.people || [] };
};

/**
 * Load whether Slack (search + send) is connected for the current user.
 *
 * Unlike Gmail's status, there's no connector/frequency here — Slack is
 * search-and-send only, not a data source.
 *
 * @example
 * await getSlackConnectionStatus();
 * // { connected: true, has_send_scope: true }
 */
export const getSlackConnectionStatus = async () => {
  const response = await fetch(businessModeApiUrl("slack/status"), {
    method: "GET",
    headers: jsonHeaders,
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error("Failed to fetch Slack status");
  }
  return response.json();
};

/**
 * Disconnect Slack for the current user.
 *
 * @example
 * await disconnectSlack();
 * // { message: "Slack disconnected", disconnected: true }
 */
export const disconnectSlack = async () => {
  const response = await fetch(businessModeApiUrl("slack/disconnect"), {
    method: "POST",
    headers: jsonHeaders,
    credentials: "include",
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error?.error || "Failed to disconnect Slack");
  }
  return response.json();
};

/**
 * Commit a user-confirmed CRM write (Attio or Salesforce, per `payload.provider`) for a task.
 *
 * `payload` is the (possibly user-edited) proposed payload the performer built:
 * { provider, object_type, match_attribute, fields, note_title, note_content, task_content, ... }.
 * Resolves to the written CRM ids, or throws with the server error message.
 *
 * @example
 * await writeCrmRecord("w::item-key::f", { provider: "attio", object_type: "people", fields: {...} });
 * // { crm_record_id: "rec_1", crm_object_type: "people", crm_note_id: "note_1", provider: "attio" }
 */
export const writeCrmRecord = async (itemKey, payload) => {
  const response = await fetch(
    businessModeApiUrl(`tasks/${encodeURIComponent(itemKey)}/crm-write`),
    {
      method: "POST",
      credentials: "include",
      headers: jsonHeaders,
      body: JSON.stringify(payload || {}),
    }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.error || "Failed to write to CRM");
    err.status = response.status;
    err.code = data?.code || null;
    throw err;
  }
  return data;
};

/**
 * Patch the recipient, subject, and/or body of an already-created Gmail draft
 * (no pipeline re-run). Any subset of the three may be passed; omitted ones
 * keep their current value.
 *
 * Used after recipient ambiguity, a missing address, or the user editing the
 * AI-drafted subject/body in-app. Resolves to the updated draft ids and
 * content, or throws with the server error message.
 *
 * @example
 * await updateEmailDraft("w::item-key::f", { toEmail: "jane@acme.com" });
 * // { resolved_recipient_email: "jane@acme.com", gmail_draft_id: "r-1", recipient_ambiguous: false }
 *
 * @example
 * await updateEmailDraft("w::item-key::f", { subject: "Following up", body: "Hi Jane, ..." });
 * // { subject: "Following up", draft: "Hi Jane, ...", gmail_draft_id: "r-1" }
 */
export const updateEmailDraft = async (itemKey, { toEmail, subject, body } = {}) => {
  const response = await fetch(
    businessModeApiUrl(`tasks/${encodeURIComponent(itemKey)}/email-draft`),
    {
      method: "POST",
      credentials: "include",
      headers: jsonHeaders,
      body: JSON.stringify({ to_email: toEmail, subject, body }),
    }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.error || "Failed to update draft");
    err.status = response.status;
    err.code = data?.code || null;
    throw err;
  }
  return data;
};

/**
 * Search Gmail for recipient candidates for a "Send via Gmail" click.
 *
 * Only for the unified `type: "message"` draft — the first live Gmail touch
 * in the send flow (a thread search, read-only); no draft is created here.
 * With no `query`, searches by the item's stored recipient/context (the
 * picker's initial suggestions). With a `query`, searches Gmail live by that
 * text instead — backs search-as-you-type.
 *
 * @example
 * await getEmailRecipientCandidates("w::item-key::f");
 * // { candidates: [{ email: "priya@acme.com", label: "priya@acme.com — from 'Re: Pricing'" }] }
 * await getEmailRecipientCandidates("w::item-key::f", "abhinav");
 * // { candidates: [{ email: "abhinav@starmesh.ai", label: "..." }] }
 */
export const getEmailRecipientCandidates = async (itemKey, query = "") => {
  const params = new URLSearchParams();
  if (query) params.set("query", query);
  const qs = params.toString();
  const response = await fetch(
    businessModeApiUrl(
      `tasks/${encodeURIComponent(itemKey)}/email-recipient-candidates${qs ? `?${qs}` : ""}`
    ),
    { method: "GET", credentials: "include", headers: jsonHeaders }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.error || "Failed to load recipient candidates");
    err.status = response.status;
    throw err;
  }
  return { candidates: Array.isArray(data?.candidates) ? data.candidates : [] };
};

/**
 * Send an already-created Gmail draft for this action item via the Gmail API.
 *
 * Irreversible — delivers to the recipient immediately. Resolves to the sent
 * message id, or throws with the server error message (e.g. not connected,
 * no draft saved yet, or already sent).
 *
 * @example
 * await sendEmailDraft("w::item-key::f");
 * // { sent: true, gmail_message_id: "18abc0d1", gmail_sent_at: "2026-08-17T10:30:00Z" }
 */
export const sendEmailDraft = async (itemKey) => {
  const response = await fetch(
    businessModeApiUrl(`tasks/${encodeURIComponent(itemKey)}/email-draft/send`),
    {
      method: "POST",
      credentials: "include",
      headers: jsonHeaders,
      body: JSON.stringify({}),
    }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.error || "Failed to send email");
    err.status = response.status;
    err.code = data?.code || null;
    throw err;
  }
  return data;
};
